import ShopifyObject from './ShopifyObject';
import Variant from './Variant';
import ProductOption from './ProductOption';

export default class Product extends ShopifyObject {
  /**
   * @param {ShopifyProduct} product
   */
  constructor(product) {
    super(product.id);

    this._title = product.title || '';
    this._handle = product.handle || '';
    this._description = product.description || '';
    this._publishedAt = product.published_at || new Date(0);
    this._createdAt = product.created_at || new Date(0);
    this._vendor = product.vendor || '';
    this._type = product.type || '';
    this._tags = product.tags || [];
    this._price = product.price || 0;
    this._priceMin = product.price_min || 0;
    this._priceMax = product.price_max || 0;
    this._available = product.available || false;
    this._priceVaries = product.price_varies || false;
    this._compareAtPrice = product.compare_at_price || null;
    this._compareAtPriceMin = product.compare_at_price_min || 0;
    this._compareAtPriceMax = product.compare_at_price_max || 0;
    this._compareAtPriceVaries = product.compare_at_price_varies || false;
    this._images = product.images || [];
    this._featuredImage = product.featured_image || null;
    this._url = product.url || '';
    this._options = product.options
      ? product.options.map(option => new ProductOption(option))
      : [];
    this._variants = product.variants
      ? product.variants.map(variant => new Variant(variant))
      : [];
  }

  /**
   * @returns {string}
   */
  get title() {
    return this._title;
  }

  /**
   * @returns {string}
   */
  get handle() {
    return this._handle;
  }

  /**
   * @returns {string}
   */
  get description() {
    return this._description;
  }

  /**
   * @returns {Date}
   */
  get publishedAt() {
    return this._publishedAt;
  }

  /**
   * @returns {Date}
   */
  get createdAt() {
    return this._createdAt;
  }

  /**
   * @returns {string}
   */
  get vendor() {
    return this._vendor;
  }

  /**
   * @returns {string}
   */
  get type() {
    return this._type;
  }

  /**
   * @returns {string[]}
   */
  get tags() {
    return this._tags;
  }

  /**
   * @returns {number}
   */
  get price() {
    return this._price;
  }

  /**
   * @returns {number}
   */
  get priceMin() {
    return this._priceMin;
  }

  /**
   * @returns {number}
   */
  get priceMax() {
    return this._priceMax;
  }

  /**
   * @returns {boolean}
   */
  get available() {
    return this._available;
  }

  /**
   * @returns {number}
   */
  get priceVaries() {
    return this._priceVaries;
  }

  /**
   * @returns {number}
   */
  get compareAtPrice() {
    return this._compareAtPrice;
  }

  /**
   * @returns {number}
   */
  get compareAtPriceMin() {
    return this._compareAtPriceMin;
  }

  /**
   * @returns {number}
   */
  get compareAtPriceMax() {
    return this._compareAtPriceMax;
  }

  /**
   * @returns {number}
   */
  get compareAtPriceVaries() {
    return this._compareAtPriceVaries;
  }

  /**
   * @returns {string[]}
   */
  get images() {
    return this._images;
  }

  /**
   * @returns {string}
   */
  get featuredImage() {
    return this._featuredImage;
  }

  /**
   * @returns {ProductOption[]}
   */
  get options() {
    return this._options;
  }

  /**
   * @returns {string}
   */
  get url() {
    return this._url;
  }

  /**
   * @return {Variant[]}
   */
  get variants() {
    return this._variants;
  }

  /**
   * @param {number} variantId
   * @returns {Variant|null}
   */
  findVariantById(variantId) {
    return this.variants.find(variant => variant.id === variantId) || null;
  }

  /**
   * @returns {Variant|null}
   */
  getFirstAvailableOrSelectedVariant() {
    /**
     * TODO: I think we are missing the 'get selected variant' here..
     I believe the selected variant will be determined by the url parameter "?variant=123456789" from the customer's browser address..
     We might need Humberto's getparam helper function?
     */

    return (
      this.variants.find(variant => variant.available === this._available) ||
      null
    );
  }

  /**
   * Find one, and only one match in the project JSON (using Array with option values) and return the variant (as an Object)
   * @param {Array} options List of submitted values (e.g. ['36', 'Black']). Option array must successfully match only 1 variant.
   * @returns {Object|null} The variant object once a match has been successful. Otherwise null will be returned
   */
  getVariantFromOptions(options) {
    while (options.length < this.options.length) {
      options.push(null);
    }
    return (
      this.variants.find(variant =>
        options.every((option, index) => variant.options[index] === option)
      ) || null
    );
  }

  /**
   * @param {Array} options List of submitted values (e.g. ['36', 'Black']).
   * @return {Array{Variants}|null} The variant object once a match has been successful. Otherwise null will be returned.
   */
  getPossibleVariantsFromOptions(options) {
    const variants = this.variants.filter(variant =>
      options.every((option, index) => variant.options[index] === option)
    );
    return variants.length ? variants : null;
  }

  /**
   *
   * @return {Object} - Shopify Product Object.
   */
  JSON() {
    return {
      available: this.available,
      compare_at_price: this.compareAtPrice,
      compare_at_price_max: this.compareAtPriceMax,
      compare_at_price_min: this.compareAtPriceMin,
      compare_at_price_varies: this.compareAtPriceVaries,
      created_at: this.createdAt,
      description: this.description,
      featured_image: this.featuredImage,
      handle: this.handle,
      id: super.id,
      images: this.images,
      options: this.options.length
        ? this.options.map(option => option.JSON())
        : [],
      price: this.price,
      price_max: this.priceMax,
      price_min: this.priceMin,
      price_varies: this.priceVaries,
      published_at: this.publishedAt,
      tags: this.tags,
      title: this.title,
      type: this.type,
      url: this.url,
      variants: this.variants.length
        ? this.variants.map(variant => variant.JSON())
        : [],
      vendor: this.vendor,
    };
  }
}
